import { isOpportunityId, type Opportunity, type OpportunityPage, type Taxonomy } from './opportunity';

/**
 * The CommonGround backend, read-only.
 *
 *   GET /opportunities         a page of events, keyset-paginated
 *   GET /opportunities/count   how many match the same filters
 *   GET /taxonomy              topics, goals and event-type words
 *
 * The contract is commonground-backend/docs/api/opportunities.md. Errors arrive
 * as `{ error: { code, message, details? } }` and are rethrown as `ApiError`.
 */

export const API_URL = (
  (import.meta.env.VITE_API_URL as string | undefined) || 'https://api.myclimateweek.com'
).replace(/\/+$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export const isAbort = (err: unknown): boolean =>
  err instanceof DOMException && err.name === 'AbortError';

export type QueryValue = string | number | boolean | readonly string[] | null | undefined;
export type OpportunityQuery = Record<string, QueryValue>;

/**
 * Parameters the API validates as UUIDs.
 *
 * A browser still holding a pre-migration id, or a personal event's, must not
 * turn a whole request into a 400, so anything else is dropped here.
 */
const UUID_PARAMS = new Set(['id', 'excludeId', 'anchor']);

/** Repeatable parameters are appended once per value, as Express reads them. */
export function toSearchParams(query: OpportunityQuery): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    const values = Array.isArray(value) ? value : [String(value)];
    for (const item of values) {
      if (UUID_PARAMS.has(key) && !isOpportunityId(item)) continue;
      params.append(key, item);
    }
  }
  return params;
}

async function getJson<T>(path: string, params?: URLSearchParams, signal?: AbortSignal): Promise<T> {
  const search = params?.toString();
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}${search ? `?${search}` : ''}`, {
      signal,
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    if (isAbort(err)) throw err;
    // A network failure and a CORS refusal look identical from here.
    throw new ApiError("We couldn't reach the events service", 0, null);
  }

  if (!res.ok) {
    let code: string | null = null;
    let message = `${res.status} ${res.statusText}`.trim();
    try {
      const body = (await res.json()) as { error?: { code?: string; message?: string } };
      code = body.error?.code ?? null;
      message = body.error?.message ?? message;
    } catch {
      // Not the envelope: keep the status line.
    }
    throw new ApiError(message, res.status, code);
  }

  return (await res.json()) as T;
}

export function listOpportunities(query: OpportunityQuery, signal?: AbortSignal): Promise<OpportunityPage> {
  return getJson<OpportunityPage>('/opportunities', toSearchParams(query), signal);
}

/** Parameters that page a list and mean nothing to a count. */
const PAGING_PARAMS = new Set(['cursor', 'limit', 'anchor']);

/** The same filters as the list. */
export async function countOpportunities(query: OpportunityQuery, signal?: AbortSignal): Promise<number> {
  const filters = Object.fromEntries(Object.entries(query).filter(([key]) => !PAGING_PARAMS.has(key)));
  const body = await getJson<{ total: number }>('/opportunities/count', toSearchParams(filters), signal);
  return body.total;
}

export function getTaxonomy(signal?: AbortSignal): Promise<Taxonomy> {
  return getJson<Taxonomy>('/taxonomy', undefined, signal);
}

/** Largest page the API serves. */
export const MAX_LIMIT = 100;

/**
 * Ids per request, across `id` and `excludeId`.
 *
 * The API accepts 500, but each UUID adds about 45 bytes to the request line
 * and Node refuses headers over 16 KB before validation runs, so a couple of
 * hundred is the practical ceiling — lower still behind a proxy.
 */
export const MAX_IDS_PER_REQUEST = 200;

/**
 * Every event matching `query`, following `nextCursor` to the end.
 *
 * For the small, bounded sets My Schedule asks for — a list of ids, or the
 * events within three hours of one — never for Explore's feed.
 */
export async function listAll(query: OpportunityQuery, signal?: AbortSignal): Promise<Opportunity[]> {
  const out: Opportunity[] = [];
  let cursor: string | null = null;
  do {
    const page: OpportunityPage = await listOpportunities(
      { ...query, limit: MAX_LIMIT, cursor: cursor ?? undefined },
      signal,
    );
    out.push(...page.items);
    cursor = page.nextCursor;
  } while (cursor);
  return out;
}
